import {
  Box,
  Card,
  Container,
  Divider,
  Flex,
  LoadingOverlay,
  Stack,
  Stepper,
  ThemeIcon,
  Title,
  Text,
  RingProgress,
  Button,
} from "@mantine/core";
import React, { useEffect, useRef, useState } from "react";
import OrderLicenseStep from "./OrderLicenseStep";
import SelectableLicenseList from "./SelectableLicenseList";
import { createOrder } from "@utils/api-communication/RestApiCli";
import { Order, CreateOrderDto } from "../../../../types/accountManagementTypes";
import { IconCheck } from "@tabler/icons-react";
import { navigate } from "@utils/navigationService";

const NewOrder: React.FC = () => {
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const [createdLicenses, setCreatedLicenses] = useState<any>(null);
  const [orderData, setOrderData] = useState<Order | null>(null);
  const [licenseCreateSuccess, setLicenseCreateSuccess] = useState(false);
  const [ringProgress, setRingProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const ringAnimRef = useRef<number | null>(null);

  useEffect(() => {
    if (!licenseCreateSuccess) {
      setRingProgress(0);
      return;
    }
    const duration = 4000;
    const start = performance.now();

    if (ringAnimRef.current) cancelAnimationFrame(ringAnimRef.current);

    const tick = (now: number) => {
      const elapsed = now - start;
      const pct = Math.min(100, (elapsed / duration) * 100);
      setRingProgress(pct);
      if (elapsed < duration) {
        ringAnimRef.current = requestAnimationFrame(tick);
      } else {
        ringAnimRef.current = null;
        setRingProgress(100);
        setActive(1);
        setLicenseCreateSuccess(false);
      }
    };

    ringAnimRef.current = requestAnimationFrame(tick);
    return () => {
      if (ringAnimRef.current) cancelAnimationFrame(ringAnimRef.current);
    };
  }, [licenseCreateSuccess]);

  const create = (payload: CreateOrderDto) => {
    setLoading(true);
    setError(null);
    createOrder(
      payload,
      (response) => {
        const { order, licenses } = response.data;
        setCreatedLicenses(licenses);
        setOrderData(order);
        setLoading(false);
        setLicenseCreateSuccess(true);
      },
      (err) => {
        console.error("createOrder failed", err);
        setLoading(false);
        setError(err?.response?.data?.message || err?.message || "Failed to create order");
      },
    );
  };

  const licenseCreateSuccessCard = () => (
    <Card mt={"md"} withBorder shadow="lg">
      <Stack w="100%" h="100%" justify="center" align="center">
        <RingProgress
          size={100}
          thickness={8}
          sections={[{ value: ringProgress, color: "green" }]}
          label={
            <Flex justify="center" align="center" h="100%" w="100%">
              <ThemeIcon radius="xl" size="xl" color="green">
                <IconCheck />
              </ThemeIcon>
            </Flex>
          }
        />
        <Text fz={"lg"} ta="center">
          {"Order received successfully! You can now assign licenses."}
        </Text>
      </Stack>
    </Card>
  );

  return (
    <Container>
      <Flex justify={"space-between"} align="center" my="md">
        <Title>Create Order</Title>
        <Divider orientation="vertical" mx="md" />
        <Stepper
          active={active}
          onStepClick={setActive}
          allowNextStepsSelect={false}
          my={"md"}
          size="sm"
          w={"70%"}
        >
          <Stepper.Step
            label="Select Licenses"
            description="Select the number and type of licenses"
          />

          <Stepper.Step
            label="Enter Customer Details"
            description="This step can be completed later"
          />
        </Stepper>
      </Flex>
      <Box pos="relative">
        <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
        {error && (
          <Text c="red" mb="md">
            {error}
          </Text>
        )}
        {active === 0 &&
          (licenseCreateSuccess ? (
            licenseCreateSuccessCard()
          ) : (
            <OrderLicenseStep onCreate={create} />
          ))}
        {active === 1 && (
          <Stack>
            <SelectableLicenseList licenses={createdLicenses} order={orderData} />
            <Button mt="md" variant="subtle" onClick={() => navigate("/orders")}>
              Back to Orders
            </Button>
          </Stack>
        )}
      </Box>
    </Container>
  );
};

export default NewOrder;
